import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { subscriptionViewModel } from '../../../view-model/professional/subscription-view-model';
import subscriptionStore from '../../../store/subscription-store';
import { Plan, PlanName } from '../../../model/professional/subscription-model';
import toastStore from '../../../store/toast-store';

interface SubscriptionStepProps {
  onPlanSelected: (planId: string) => void;
  onBack: () => void;
}

const SubscriptionStep: React.FC<SubscriptionStepProps> = observer(({ onPlanSelected, onBack }) => {
  const [selectedPlan, setSelectedPlan] = useState<PlanName | null>(null);

  useEffect(() => {
    subscriptionViewModel.fetchPlans();
  }, []);

  const handleSelectPlan = (planId: PlanName) => {
    setSelectedPlan(planId);
  };

  const handleContinue = () => {
    if (!selectedPlan) {
      toastStore.error('Veuillez sélectionner un plan');
      return;
    }

    // Passer au step suivant avec le plan sélectionné
    onPlanSelected(selectedPlan);
  };

  const renderPlanCard = (plan: Plan) => {
    const isSelected = selectedPlan === plan.id;
    const isPopular = plan.id === 'premium';

    return (
      <div
        key={plan.id}
        className={`relative p-6 rounded-2xl border-2 transition-all cursor-pointer ${
          isSelected
            ? 'border-primary bg-primary/5 dark:bg-primary/10 shadow-lg scale-105'
            : 'border-gray-200 dark:border-gray-700 hover:border-primary/50 dark:hover:border-primary/50'
        }`}
        onClick={() => handleSelectPlan(plan.id)}
      >
        {isPopular && (
          <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
            <span className="bg-gradient-to-r from-primary to-primary-light text-white text-xs font-semibold px-4 py-1 rounded-full">
              Populaire
            </span>
          </div>
        )}

        {/* Radio button */}
        <div className="absolute top-4 right-4">
          <div
            className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
              isSelected
                ? 'border-primary bg-primary'
                : 'border-gray-300 dark:border-gray-600'
            }`}
          >
            {isSelected && (
              <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clipRule="evenodd"
                />
              </svg>
            )}
          </div>
        </div>

        <div className="mb-4">
          <h3 className="text-2xl font-bold text-gray-900 dark:text-white">{plan.name}</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{plan.description}</p>
        </div>

        <div className="mb-6">
          <span className="text-4xl font-bold text-gray-900 dark:text-white">{plan.price}€</span>
          <span className="text-gray-600 dark:text-gray-400">/{plan.period}</span>
        </div>

        <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3 mb-6">
          <p className="text-sm font-semibold text-green-800 dark:text-green-300 flex items-center">
            <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
            14 jours d'essai gratuit
          </p>
        </div>

        <ul className="space-y-3">
          <li className="flex items-start">
            <svg className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
            <span className="text-sm text-gray-700 dark:text-gray-300">
              {plan.features.maxCompanies === -1 ? 'Entreprises illimitées' : `${plan.features.maxCompanies} entreprise${plan.features.maxCompanies > 1 ? 's' : ''}`}
            </span>
          </li>
          <li className="flex items-start">
            <svg className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
            <span className="text-sm text-gray-700 dark:text-gray-300">
              {plan.features.maxCollaborators === -1 ? 'Collaborateurs illimités' : `${plan.features.maxCollaborators} collaborateur${plan.features.maxCollaborators > 1 ? 's' : ''}`}
            </span>
          </li>
          <li className="flex items-start">
            <svg className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
            <span className="text-sm text-gray-700 dark:text-gray-300">
              {plan.features.maxActivePromoCodes === -1 || plan.features.maxActivePromoCodes === 999999 ? 'Codes promo illimités' : `${plan.features.maxActivePromoCodes} code${plan.features.maxActivePromoCodes > 1 ? 's' : ''} promo`}
            </span>
          </li>
          {plan.features.onlinePayment && (
            <li className="flex items-start">
              <svg className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
              <span className="text-sm text-gray-700 dark:text-gray-300">Paiement en ligne</span>
            </li>
          )}
          {plan.features.advancedAnalytics && (
            <li className="flex items-start">
              <svg className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
              <span className="text-sm text-gray-700 dark:text-gray-300">Statistiques avancées</span>
            </li>
          )}
        </ul>
      </div>
    );
  };

  return (
    <div className="py-6">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
          Choisissez votre formule
        </h2>
        <p className="text-gray-600 dark:text-gray-400">
          Profitez de 14 jours d'essai gratuit, sans engagement
        </p>
      </div>

      {subscriptionViewModel.isLoading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {subscriptionStore.availablePlans.map((plan) => renderPlanCard(plan))}
          </div>

          <div className="flex items-center justify-between pt-6 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={onBack}
              className="px-6 py-3 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              Retour
            </button>

            <button
              onClick={handleContinue}
              disabled={!selectedPlan}
              className={`px-8 py-3 rounded-lg font-semibold transition-all ${
                selectedPlan
                  ? 'bg-gradient-to-r from-primary to-primary-light hover:from-primary-dark hover:to-primary text-white shadow-lg hover:shadow-xl transform hover:scale-105'
                  : 'bg-gray-300 dark:bg-gray-700 text-gray-500 cursor-not-allowed'
              }`}
            >
              Continuer
            </button>
          </div>
        </>
      )}
    </div>
  );
});

export default SubscriptionStep;
